/**
 * The default catalog editor tab — the split the 07-29 spec §12 recommended
 * ("Impact is the default because the ripple is the one thing Excel cannot do")
 * and that shipped inverted. Components left, live price impact right.
 *
 * One quarter at a time, deliberately: that is the trade Option B makes for
 * putting the ripple on screen while the PM types. The multi-quarter view is
 * GridView, the second tab.
 */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { resolveAt } from '../../pricing/resolveAt';
import { microsToDollars } from '../../pricing/money';
import { normalizeAmount } from '../../catalog/readSheet';
import { unscaleDecimal } from '../../catalog/planWrites';
import { productSkusOf } from '../../catalog/catalogSkus';
import { formatDollars, impactDisplay } from './format';
import { usedByCount, groupByCategory } from './categories';

/** Matches the 300ms debounce useConfigurationLogic.jsx:547 already uses — but
 *  that one is an inline literal, not an export, so this is a SECOND 300ms in
 *  the codebase, not a shared constant, until someone extracts it.
 *  previewImpact runs the engine over the whole seed, and before this it ran
 *  once per stage — not once per keystroke. */
const IMPACT_DEBOUNCE_MS = 300;

export function ImpactView({
  vendor, user, seed, components, categories, editableCategories, quarter, preview, stage, busy,
}) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [editing, setEditing] = useState(null);       // { sku } | null
  const [editValue, setEditValue] = useState('');
  const [filter, setFilter] = useState('');

  /**
   * Free-text narrowing on componentSku, restoring the merge-base
   * `CatalogPage`'s filter box (`git show 80559012:src/admin/catalog/
   * CatalogPage.jsx`) that Task 6's plan dropped assuming the views would
   * grow their own (whole-branch review finding F3) — with the real AIC seed
   * running 30 near-identical `CMP-BASE-VCH-5000-J108-*` SKUs into a single
   * Uncategorised bucket, there was no way to find one.
   *
   * Applied to `components` BEFORE `groupByCategory`, not after: a bucket's
   * header count (`group.skus.length`) and whether it renders at all both
   * come out of `groupByCategory` for free that way — a category with no
   * matching members empties out and is omitted by its own "empty bucket
   * omitted" rule (categories.test.js), Uncategorised included, rather than
   * this view needing a second post-hoc emptiness check.
   */
  const filteredComponents = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return components;
    return (components ?? []).filter((c) => c.componentSku.toLowerCase().includes(needle));
  }, [components, filter]);

  const groups = useMemo(
    () => groupByCategory(filteredComponents, categories, seed),
    [filteredComponents, categories, seed],
  );

  const toggle = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Finding F5: "focus returns to the cell" (spec) — the cell that OPENED the
  // editor, restored once the editor actually closes (Escape, Enter, or the
  // quarter-change close below). `cellRefs` is populated by the display
  // cell's own ref callback below, so the node handed to `.focus()` is
  // whatever most recently rendered at that key — never a stale reference to
  // a node React already replaced. The effect fires on `editing` transitions
  // (post-commit, after the display `<td>` — `role="button"`, `tabIndex={0}`
  // — has replaced the `<input>` and can actually receive focus), not
  // synchronously inside closeEditor, which would try to focus a node that
  // still has the OLD render's attributes.
  const cellRefs = useRef(new Map());
  const lastEditedKey = useRef(null);

  useEffect(() => {
    if (editing || !lastEditedKey.current) return;
    cellRefs.current.get(lastEditedKey.current)?.focus();
    lastEditedKey.current = null;
  }, [editing]);

  const cellFor = (sku) => {
    const resolution = resolveAt(components, sku, quarter);
    if (!resolution) return { text: '—', editText: '' };
    return {
      text: formatDollars(microsToDollars(resolution.row.value)),
      // Restated exactly, never re-rounded: unscaleDecimal is the exact inverse
      // of the scaleDecimal(_, 6) the write path applies, so an untouched edit
      // compares EQUAL and is a genuine no-op. 236 of the real seed's 345 rows
      // carry sub-cent precision — toFixed(2) here silently rewrites them.
      editText: unscaleDecimal(resolution.row.value, 6),
    };
  };

  const openEditor = (sku, editText) => {
    lastEditedKey.current = sku;
    setEditing({ sku });
    setEditValue(editText ?? '');
  };
  const closeEditor = () => { setEditing(null); setEditValue(''); };

  /**
   * Finding F4: `editValue` is pre-filled from THIS quarter's resolution at
   * `openEditor` time, but nothing cleared `editing` when the `quarter` prop
   * itself changed — click a cell, change the quarter selector, press Enter,
   * and the OLD quarter's typed amount landed on the NEW quarter
   * (`commitEdit` above reads `quarter` fresh, but `editValue` is stale).
   * Closes the editor outright rather than trying to re-resolve it against
   * the new quarter, matching Escape's behaviour: the safe read is "the PM
   * must look at the new quarter's value and decide again," not a guess.
   *
   * `lastEditedKey` is cleared FIRST so the focus-restore effect above does
   * not fire for this close — the quarter `<select>` the PM just used, not
   * the table, is where focus belongs after a quarter change.
   */
  useEffect(() => {
    lastEditedKey.current = null;
    setEditing(null);
    setEditValue('');
  }, [quarter]);

  const commitEdit = () => {
    const amount = normalizeAmount(editValue);
    if (amount === null) return;
    stage({
      vendor,
      sourceLabel: `Manual edit by ${user?.username ?? 'unknown'}`,
      cells: [{ ref: editing.sku, target: null, quarter, amount }],
    });
    closeEditor();
  };

  /** Enter/Space activate a cell the way a click does. Hand-rolled on the cell
   *  rather than nesting a real <button>, exactly as CatalogPage did and for
   *  the same reasons: a click fired at an ancestor never reaches a
   *  descendant's listener, and jsdom does not implement a native button's
   *  Enter/Space activation. */
  const onCellKeyDown = (event, sku, editText) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    event.preventDefault();
    openEditor(sku, editText);
  };

  const onInputKeyDown = (event) => {
    if (event.key === 'Enter') { event.preventDefault(); commitEdit(); }
    if (event.key === 'Escape') { event.preventDefault(); closeEditor(); }
  };

  const [live, setLive] = useState(null);

  useEffect(() => {
    if (!editing) { setLive(null); return undefined; }
    const amount = normalizeAmount(editValue);
    // An emptied box (select-all, delete) is not "no edit yet" — it is an edit
    // whose amount cannot be parsed, and the panel says "live". Leaving the
    // previous preview on screen here would show a ripple computed from a
    // value no longer in the input.
    if (amount === null) { setLive(null); return undefined; }

    const timer = setTimeout(() => {
      setLive(preview({
        vendor,
        sourceLabel: 'live preview',
        cells: [{ ref: editing.sku, target: null, quarter, amount }],
      }));
    }, IMPACT_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [editing, editValue, preview, quarter, vendor]);

  /** Grouped by QUARTER, not by component. `ImpactRow` (previewImpact.ts:26-36)
   *  has no `componentSku` — it never did, and it should not gain one just for
   *  this table. In live preview the draft always has exactly ONE cell, so
   *  every row in `live.impact` was caused by the SAME component; grouping by
   *  component would be degenerate, one group headed by whatever the row
   *  happened to carry. The component worth naming is the one being edited,
   *  which the view already knows as `editing.sku` and states once above the
   *  table, not per row. `previewImpact` runs over EVERY publish quarter for
   *  one edited cell (`publishQuarters(vendor)`, useCatalogDraft.js:119-122),
   *  so a dozen-plus rows per SKU differing only by quarter and kind is the
   *  normal shape — quarter is what actually varies here, and reads as the
   *  useful axis to fold on. */
  const impactGroups = useMemo(() => {
    const byQuarter = new Map();
    for (const row of live?.impact ?? []) {
      if (!byQuarter.has(row.quarter)) byQuarter.set(row.quarter, []);
      byQuarter.get(row.quarter).push(row);
    }
    return [...byQuarter.entries()];
  }, [live]);

  // `ImpactRow.productSku` repeats once per (quarter, kind) — previewImpact
  // runs over every publish quarter for one edited cell, and a SKU can move
  // both its price and its discount in the same quarter. Deduped the same way
  // ReviewPanel's commit gate counts rows, not raw row length, or the stated
  // count is bigger than the SKUs that exist to move.
  const movedSkuCount = useMemo(
    () => new Set((live?.impact ?? []).map((row) => row.productSku)).size,
    [live],
  );

  return (
    <div className="flex gap-0">
      <div className="flex-[1.3] min-w-0 overflow-x-auto pr-3">
        <div className="mb-3">
          <label htmlFor="catalog-filter" className="block text-sm text-gray-400 mb-1">
            Filter by component SKU
          </label>
          <input
            id="catalog-filter"
            type="text"
            aria-label="Filter components"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5"
          />
        </div>
        <table className="text-sm w-full" aria-label={`Component costs for ${quarter}`}>
          <thead className="text-gray-400 text-left">
            <tr>
              <th scope="col">Component</th>
              <th scope="col">Cost</th>
              <th scope="col">Used by</th>
            </tr>
          </thead>
          {groups.map((group) => {
            const open = !collapsed.has(group.id);
            const editable = (editableCategories ?? []).includes(group.id);
            return (
              <tbody key={group.id}>
                <tr>
                  <th scope="colgroup" colSpan={3} className="text-left pt-3">
                    <button
                      type="button"
                      onClick={() => toggle(group.id)}
                      aria-expanded={open}
                      className="text-orange-400 text-xs tracking-wide uppercase"
                    >
                      {open ? '▾' : '▸'} {group.label} · {group.skus.length}
                    </button>
                  </th>
                </tr>
                {open && group.skus.map((sku) => {
                  const cell = cellFor(sku);
                  const isEditing = editing?.sku === sku;
                  const label = `Edit cost for ${sku} ${quarter}`;
                  return (
                    <tr key={sku} className="border-t border-gray-800">
                      <th scope="row" className="font-normal">{sku}</th>
                      {isEditing ? (
                        <td>
                          <input
                            autoFocus
                            aria-label={label}
                            value={editValue}
                            disabled={busy}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={onInputKeyDown}
                            className="bg-gray-900 border border-orange-400 rounded px-1 w-28 text-right"
                          />
                        </td>
                      ) : editable ? (
                        <td
                          ref={(el) => {
                            if (el) cellRefs.current.set(sku, el);
                            else cellRefs.current.delete(sku);
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={label}
                          onClick={() => openEditor(sku, cell.editText)}
                          onKeyDown={(e) => onCellKeyDown(e, sku, cell.editText)}
                          className="cursor-pointer px-2 text-right"
                        >
                          {cell.text}
                        </td>
                      ) : (
                        <td
                          aria-label={`${sku} cost`}
                          className="px-2 text-right text-gray-500"
                        >
                          {cell.text}
                        </td>
                      )}
                      <td aria-label={`${sku} used by`} className="px-2 text-right text-gray-400">
                        {usedByCount(seed, sku)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            );
          })}
        </table>
      </div>

      <aside className="flex-1 min-w-0 border-l border-gray-800 pl-3" aria-label="Price impact">
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Price impact — live</p>

        {live?.impactError && (
          <div role="alert" className="text-red-400 text-sm mb-2">{live.impactError}</div>
        )}

        {!live && <p className="text-gray-500 text-sm">Edit a cost to see what it moves.</p>}

        {live && !live.impactError && (
          <>
            {/* Stated plainly. 07-29 §13 measured the ripple as concentrated,
                not broad — this panel must read right at 10 rows, not 40.
                Named by the SKU being edited (editing.sku), not by row data —
                ImpactRow has no componentSku, and every row here was caused by
                this one edit anyway. Both counts are SKUs deduped by
                productSku: `movedSkuCount` above, and `productSkusOf(seed)`
                below because `seed.compositions` carries every superseded
                composition version side by side (seedBuilder.ts:286) and a raw
                `.length` overcounts the SKUs that actually exist. */}
            <p className="text-sm mb-2">
              <b className="text-green-400">{movedSkuCount}</b>
              <span className="text-gray-400">
                {' '}of {productSkusOf(seed).length} SKUs moved by editing {editing?.sku}
              </span>
            </p>
            <table className="text-sm w-full">
              <thead className="text-gray-400 text-left">
                <tr>
                  <th scope="col">SKU</th><th scope="col">What</th>
                  <th scope="col">After</th><th scope="col">Δ</th>
                </tr>
              </thead>
              {impactGroups.map(([impactQuarter, rows]) => (
                <tbody key={impactQuarter}>
                  <tr><th scope="colgroup" colSpan={4} className="text-left pt-2 text-xs text-gray-500">
                    {impactQuarter} ▸ {rows.length}
                  </th></tr>
                  {rows.map((row) => {
                    // Its OWN units — a discount row is a fraction, not dollars.
                    const shown = impactDisplay(row);
                    return (
                      // kind belongs in the key, matching ReviewPanel.jsx: a
                      // single (sku, quarter) can move BOTH its price and its
                      // discount, and a key without kind collides and drops one.
                      // The "What" column is the same reason, on screen: within
                      // one quarter group two rows can share a SKU and differ
                      // only by kind, and `shown.what` (impactDisplay, the SAME
                      // formatter ReviewPanel.jsx renders) is what tells them
                      // apart rather than leaving it to the reader to infer from
                      // a $ vs a % sign.
                      <tr
                        key={`${row.productSku}#${row.quarter}#${row.kind ?? 'price'}`}
                        className="border-t border-gray-800"
                      >
                        <th scope="row" className="font-normal">{row.productSku}</th>
                        <td className="text-gray-400">{shown.what}</td>
                        <td className="text-right">{shown.after}</td>
                        <td className="text-right text-green-400">{shown.delta}</td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </>
        )}
      </aside>
    </div>
  );
}

export default ImpactView;
